/**
 * The rush hour rules, ported from `apps/puzzletypes/rush_hour/board.py`. The two are kept at
 * parity by the shared fixtures in `shared/fixtures/rush_hour/`.
 *
 * Pure: it reads rows and moves and returns positions. A move is a vehicle and a signed
 * distance — `B+2` slides B two cells right (or down, for an upright vehicle) — and a slide
 * of any length is one move.
 */
import { EMPTY, MOVE_PATTERN, RED, SIZE, WALL, type Direction } from "./types";

export interface Vehicle {
  letter: string;
  /** Top-left cell. */
  row: number;
  col: number;
  length: number;
  horizontal: boolean;
}

export type State = Record<string, Vehicle>;

export interface Lot {
  /** Wall cells as "row,col". */
  walls: Set<string>;
}

const cellKey = (row: number, col: number) => `${row},${col}`;

export function vehicleCells(vehicle: Vehicle): [number, number][] {
  return Array.from({ length: vehicle.length }, (_, i) =>
    vehicle.horizontal ? [vehicle.row, vehicle.col + i] : [vehicle.row + i, vehicle.col],
  );
}

/** Folds the empty alias 'o' to '.'. */
export function normalizeRows(rows: readonly string[]): string[] {
  return rows.map((row) => row.replace(/o/g, EMPTY));
}

/** Every letter and the cells it covers, in reading order. */
export function letterCells(rows: readonly string[]): Map<string, [number, number][]> {
  const found = new Map<string, [number, number][]>();
  normalizeRows(rows).forEach((row, r) => {
    Array.from(row).forEach((char, c) => {
      if (char >= "A" && char <= "Z") {
        const cells = found.get(char) ?? [];
        cells.push([r, c]);
        found.set(char, cells);
      }
    });
  });
  return found;
}

/** The vehicle a letter's cells describe, or `null` when they are not a straight run of 2+. */
export function shapeOf(letter: string, cells: [number, number][]): Vehicle | null {
  const length = cells.length;
  if (length < 2) return null;
  const rows = new Set(cells.map(([r]) => r));
  const cols = new Set(cells.map(([, c]) => c));
  const run = (values: Set<number>) => {
    const start = Math.min(...values);
    return Array.from({ length }, (_, i) => start + i).every((v) => values.has(v)) ? start : null;
  };
  if (rows.size === 1) {
    const start = run(cols);
    if (start !== null) return { letter, row: cells[0][0], col: start, length, horizontal: true };
  }
  if (cols.size === 1) {
    const start = run(rows);
    if (start !== null) return { letter, row: start, col: cells[0][1], length, horizontal: false };
  }
  return null;
}

/** Rows that match the schema: six of six characters of the notation. */
export function wellFormed(rows: readonly string[] | undefined): rows is string[] {
  return (
    Array.isArray(rows) &&
    rows.length === SIZE &&
    rows.every((row) => typeof row === "string" && /^[.oxA-Z]{6}$/.test(row))
  );
}

/** The walls and the vehicles, or `null` when a vehicle is not a straight run. */
export function parseBoard(rows: readonly string[]): { lot: Lot; state: State } | null {
  if (!wellFormed(rows)) return null;
  const grid = normalizeRows(rows);
  const walls = new Set<string>();
  grid.forEach((row, r) =>
    Array.from(row).forEach((char, c) => {
      if (char === WALL) walls.add(cellKey(r, c));
    }),
  );
  const state: State = {};
  for (const [letter, cells] of letterCells(grid)) {
    const vehicle = shapeOf(letter, cells);
    if (!vehicle) return null;
    state[letter] = vehicle;
  }
  return { lot: { walls }, state };
}

/** `"B-2"` → `["B", -2]`, or `null` when the token is not a move. */
export function parseMove(move: string): [string, number] | null {
  const match = MOVE_PATTERN.exec(move);
  if (!match) return null;
  return [match[1], Number(match[3]) * (match[2] === "+" ? 1 : -1)];
}

export function formatMove(letter: string, delta: number): string {
  return `${letter}${delta > 0 ? "+" : "-"}${Math.abs(delta)}`;
}

/** The red car's nose touches the right edge: the exit is open and it drives out. */
export function isSolved(state: State): boolean {
  const red = state[RED];
  return Boolean(red && red.horizontal && red.col + red.length === SIZE);
}

function occupiedByOthers(lot: Lot, state: State, letter: string): Set<string> {
  const taken = new Set(lot.walls);
  for (const vehicle of Object.values(state)) {
    if (vehicle.letter === letter) continue;
    for (const [r, c] of vehicleCells(vehicle)) taken.add(cellKey(r, c));
  }
  return taken;
}

/** How far a vehicle can go back (negative) and forth (positive) from where it stands. */
export function reach(lot: Lot, state: State, letter: string): { min: number; max: number } {
  const vehicle = state[letter];
  if (!vehicle || isSolved(state)) return { min: 0, max: 0 };
  const taken = occupiedByOthers(lot, state, letter);
  const free = (r: number, c: number) =>
    r >= 0 && c >= 0 && r < SIZE && c < SIZE && !taken.has(cellKey(r, c));
  const { row, col, length, horizontal } = vehicle;
  let max = 0;
  while (horizontal ? free(row, col + length + max) : free(row + length + max, col)) max += 1;
  let min = 0;
  while (horizontal ? free(row, col + min - 1) : free(row + min - 1, col)) min -= 1;
  return { min, max };
}

/** One slide, or `null` when the vehicle is unknown or anything stands in the way. */
export function slide(lot: Lot, state: State, letter: string, delta: number): State | null {
  const vehicle = state[letter];
  if (!vehicle || delta === 0) return null;
  // Once the red car has left, the game is over: there is nothing left to move.
  const { min, max } = reach(lot, state, letter);
  if (delta < min || delta > max) return null;
  const moved = vehicle.horizontal
    ? { ...vehicle, col: vehicle.col + delta }
    : { ...vehicle, row: vehicle.row + delta };
  return { ...state, [letter]: moved };
}

export interface Replay {
  state: State;
  /** The moves actually played (a prefix of the input). */
  moves: string[];
  /** Index of the first move that could not be played, or `null` when all were legal. */
  illegalAt: number | null;
}

/** Make the slides from `start`, stopping at the first illegal one. */
export function replay(lot: Lot, start: State, moves: readonly string[]): Replay {
  let state = start;
  for (let index = 0; index < moves.length; index += 1) {
    const parsed = parseMove(moves[index]);
    const next = parsed ? slide(lot, state, parsed[0], parsed[1]) : null;
    if (!next) return { state, moves: moves.slice(0, index), illegalAt: index };
    state = next;
  }
  return { state, moves: [...moves], illegalAt: null };
}

/**
 * Adds a slide to a log. Two slides of the same vehicle in a row are one slide, as every
 * published move count reads them: they merge, and a slide that undoes the previous one
 * leaves no move at all.
 */
export function appendMove(moves: readonly string[], move: string): string[] {
  const parsed = parseMove(move);
  const last = moves.length > 0 ? parseMove(moves[moves.length - 1]) : null;
  if (!parsed || !last || last[0] !== parsed[0]) return [...moves, move];
  const delta = last[1] + parsed[1];
  const head = moves.slice(0, -1);
  return delta === 0 ? head : [...head, formatMove(parsed[0], delta)];
}

/** The signed step an arrow means for a vehicle, or 0 when it points across its lane. */
export function stepFor(vehicle: Vehicle | undefined, direction: Direction): number {
  if (!vehicle) return 0;
  if (vehicle.horizontal) return direction === "l" ? -1 : direction === "r" ? 1 : 0;
  return direction === "u" ? -1 : direction === "d" ? 1 : 0;
}

/**
 * The empty cells a vehicle can reach, each with the slide that brings its nearest end
 * there: what a tap on that cell plays.
 */
export function targets(
  lot: Lot,
  state: State,
  letter: string,
): { row: number; col: number; delta: number }[] {
  const vehicle = state[letter];
  if (!vehicle) return [];
  const { min, max } = reach(lot, state, letter);
  const found: { row: number; col: number; delta: number }[] = [];
  const { row, col, length, horizontal } = vehicle;
  for (let delta = min; delta <= max; delta += 1) {
    if (delta === 0) continue;
    const offset = delta > 0 ? length - 1 + delta : delta;
    found.push(horizontal ? { row, col: col + offset, delta } : { row: row + offset, col, delta });
  }
  return found;
}

/** The lot as rows again, with the vehicles where they now stand. */
export function render(lot: Lot, state: State): string[] {
  const grid = Array.from({ length: SIZE }, () => new Array<string>(SIZE).fill(EMPTY));
  for (const key of lot.walls) {
    const [r, c] = key.split(",").map(Number);
    grid[r][c] = WALL;
  }
  for (const vehicle of Object.values(state)) {
    for (const [r, c] of vehicleCells(vehicle)) grid[r][c] = vehicle.letter;
  }
  return grid.map((row) => row.join(""));
}
